import { CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import useOnlineStatus from '../../hooks/useOnlineStatus';
import useServerStatus from '../../hooks/useServerStatus';

function ToastStack({ toasts = [], onDismiss }) {
  const isOnline = useOnlineStatus();
  const isServerOnline = useServerStatus();
  const [dismissedToastKeys, setDismissedToastKeys] = useState([]);
  const isConnected = isOnline && isServerOnline === true;
  const toastKeys = toasts
    .filter((toast) => toast?.message)
    .map(getToastKey)
    .join('|');

  useEffect(() => {
    setDismissedToastKeys([]);
  }, [toastKeys]);

  function handleDismiss(toast) {
    if (onDismiss) {
      onDismiss(toast.id);
      return;
    }

    setDismissedToastKeys((currentKeys) => [...currentKeys, getToastKey(toast)]);
  }

  const visibleToasts = toasts.filter((toast) => {
    if (!toast?.message || dismissedToastKeys.includes(getToastKey(toast))) {
      return false;
    }

    return !shouldHideConnectionToast(toast, { isOnline, isConnected });
  });

  if (!visibleToasts.length) {
    return null;
  }

  return (
    <div className="fixed right-4 top-4 z-[70] grid w-[calc(100%-2rem)] max-w-sm gap-2 sm:right-6 sm:top-6">
      {visibleToasts.map((toast) => (
        <Toast key={getToastKey(toast)} toast={toast} onDismiss={handleDismiss} />
      ))}
    </div>
  );
}

function Toast({ toast, onDismiss }) {
  const tone = toast.tone || 'info';
  const Icon = getToastIcon(tone);
  const toneClassName = getToastToneClassName(tone);

  return (
    <div
      className={`flex items-center gap-3 rounded-[16px] border bg-white px-4 py-3 text-[0.9rem] shadow-[0_18px_42px_rgba(var(--theme-shadow-rgb),0.13)] backdrop-blur animate-[toast-in_180ms_ease-out] ${toneClassName}`}
      role={tone === 'error' ? 'alert' : 'status'}
    >
      <span className="grid h-7 w-7 shrink-0 place-items-center self-center rounded-full bg-white/70">
        <Icon className="h-4 w-4" aria-hidden="true" />
      </span>
      <p className="m-0 min-w-0 flex-1 self-center leading-6">{toast.message}</p>
      <button
        className="grid h-7 w-7 shrink-0 place-items-center self-center rounded-full text-current opacity-70 transition hover:bg-white/70 hover:opacity-100"
        type="button"
        onClick={() => onDismiss(toast)}
        aria-label="Dismiss notification"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}

function getToastKey(toast) {
  return `${toast.id || toast.tone || 'toast'}:${toast.message || ''}`;
}

function shouldHideConnectionToast(toast, { isOnline, isConnected }) {
  const message = String(toast.message || '').toLowerCase();

  if (message.includes('you are offline right now')) {
    return isOnline;
  }

  if (message.includes('unable to reach the server right now')) {
    return isConnected;
  }

  return false;
}

function getToastIcon(tone) {
  if (tone === 'success') {
    return CheckCircle2;
  }

  if (tone === 'error') {
    return XCircle;
  }

  return Info;
}

function getToastToneClassName(tone) {
  if (tone === 'success') {
    return 'border-[#d8ead5] bg-[#f3faf1] text-[#3f6f42]';
  }

  if (tone === 'error') {
    return 'border-[#f0d7d1] bg-[#fff3f0] text-[#9a4e42]';
  }

  return 'border-[#e7ddd4] bg-[#fffaf5] text-[var(--theme-text-subtle)]';
}

export default ToastStack;
